import { formErrorMessages } from '../../../util/constants';

/**
 * Disables Account Search Form input fields based on touched field & search criteria.
 *
 * @param touchedField - currently touched field
 * @param formValues
 * @return {string[]|*[]} - array of strings
 */
export const accountSearchFormCriteriaValidation = (touchedField, formValues) => {
  const { item } = touchedField;

  if (item === 'Zip') {
    if (formValues) {
      if (formValues.CompanyName) {
        return ['CompanyName', 'Zip'];
      }

      if (formValues.AptNumber) {
        return ['Zip', 'StreetNumber', 'StreetName', 'AptNumber'];
      }

      if (formValues.StreetNumber || formValues.StreetName) {
        return ['Zip', 'StreetNumber', 'StreetName', 'AptNumber'];
      }

      if (formValues.FirstName || formValues.LastName) {
        return ['FirstName', 'LastName', 'Zip'];
      }
    }
    return [item, 'LastName', 'FirstName', 'StreetName', 'CompanyName', 'StreetNumber'];
  }

  if (item === 'CompanyName') {
    return [item, 'Zip'];
  }

  if (item === 'PhoneNumber' || item === 'AccountNumber' || item === 'Username') {
    return [item];
  }

  if (item === 'City' || item === 'State') {
    return ['StreetNumber', 'StreetName', 'State', 'City', 'AptNumber'];
  }

  if (item === 'StreetNumber' || item === 'StreetName') {
    return ['City', 'State', 'Zip', 'StreetNumber', 'StreetName', 'AptNumber'];
  }

  if (item === 'AptNumber') {
    return ['StreetNumber', 'StreetName', 'State', 'City', 'AptNumber', 'Zip'];
  }

  if (item === 'FirstName' || item === 'LastName') {
    return ['FirstName', 'LastName', 'Zip'];
  }
};

/**
 * Enables and disables account search button based on form validation.
 *
 * @param formData
 * @param isInvalid - is form invalid
 * @return {boolean}
 */
export const isFindAccountButtonEnabled = (formData, isInvalid) => {
  const {
    PhoneNumber,
    AccountNumber,
    AptNumber,
    LastName,
    FirstName,
    Username,
    CompanyName,
    StreetNumber,
    StreetName,
    Zip,
    City,
    State,
  } = formData;

  if (isInvalid) {
    return false;
  }

  if (PhoneNumber || AccountNumber || Username) {
    return true;
  }

  if (FirstName && LastName && Zip) {
    return true;
  }

  if (CompanyName && Zip) {
    return true;
  }

  if ((StreetNumber && StreetName && City && State) || (StreetNumber && StreetName && City && AptNumber && State)) {
    return true;
  }

  return !!((StreetNumber && StreetName && Zip) || (StreetNumber && StreetName && Zip && AptNumber));
};

/**
 * Enables user's search button based on form validation.
 *
 * @param formData
 * @return {boolean}
 */
export const isSearchUserButtonEnabled = formData => {
  if (formData && formData.values) {
    const { values } = formData;

    if (values.AgentId) {
      return true;
    }

    if (values.Email) {
      return true;
    }

    return !!(values.LastName && values.FirstName);
  }
};

/**
 * Validates account search form input fields based on criteria
 *
 * @param values - form values
 * @return {errors} - input field errors
 */
export const validate = values => {
  const errors = {};

  if (values.Zip) {
    if (
      (!values.FirstName || !values.LastName) &&
      (!values.StreetName || !values.StreetNumber) &&
      !values.CompanyName
    ) {
      errors.Zip = formErrorMessages.zipError;
    }
  }

  if (values.State) {
    if (!values.StreetName || !values.StreetNumber || !values.City) {
      errors.State = formErrorMessages.stateError;
    }
  }

  if (values.City) {
    if (!values.StreetNumber || !values.StreetName || !values.State) {
      errors.City = formErrorMessages.cityError;
    }
  }

  if (values.CompanyName) {
    if (!values.Zip) {
      errors.CompanyName = formErrorMessages.companyError;
    }
  }

  if (values.StreetName) {
    if (!values.StreetNumber || (!values.Zip && (!values.City || !values.State))) {
      errors.StreetName = formErrorMessages.streetNameError;
    }
  }

  if (values.StreetNumber) {
    if (!values.StreetName || (!values.Zip && (!values.City || !values.State))) {
      errors.StreetNumber = formErrorMessages.streetNumberError;
    }
  }

  if (values.AptNumber) {
    if (!values.StreetName || !values.StreetNumber || (!values.Zip && (!values.City || !values.State))) {
      errors.AptNumber = formErrorMessages.apartmentNumberError;
    }
  }

  if (values.FirstName) {
    if (!values.Zip || !values.LastName) {
      errors.FirstName = formErrorMessages.firstNameError;
    }
  }

  if (values.LastName) {
    if (!values.Zip || !values.FirstName) {
      errors.LastName = formErrorMessages.lastNameError;
    }
  }

  return errors;
};

/**
 * Validates csr search form input fields based on criteria
 *
 * @param values - form values
 * @return {errors} - input field errors
 */
export const validateCsrSearchForm = values => {
  const errors = {};

  if (values.FirstName) {
    if (!values.LastName) {
      errors.FirstName = formErrorMessages.csrFirstNameError;
    }
  }

  if (values.LastName) {
    if (!values.FirstName) {
      errors.LastName = formErrorMessages.csrLastNameError;
    }
  }

  return errors;
};
